import * as React from 'react';
import { Reveal, FloatingStuds } from '../anim.jsx';
import { Icon } from '../icons.jsx';
import { Button, Badge, Tag, Card, Placeholder, ProviderPhoto } from '../ui.jsx';
import { PROVIDERS, AGE_GROUPS, AGE_GROUP_RANGES, SCHEDULES } from '../data';

// Homepage - family view.

const HomeProviderCard = ({ p, go, delay }) => (
  <Reveal delay={delay} style={{ display: 'block' }}>
    <Card hoverable onClick={() => go('profile', { providerId: p.id })}>
      <div style={{ padding: 10 }}>
        {p.image
          ? <ProviderPhoto src={p.image} alt={`${p.name} childcare space`} radius={10} height={170} label={p.neighborhood}/>
          : <Placeholder tone={p.tone} radius={10} height={170} label={p.neighborhood}/>}
      </div>
      <div style={{ padding: '4px 16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {p.status === 'open'
            ? <Badge tone="open" icon="live">{p.spots} open {p.spots === 1 ? 'spot' : 'spots'}</Badge>
            : <Badge tone="wait">Waitlist Only</Badge>}
          {p.subsidy && <Badge tone="brand" icon="shield">Subsidy</Badge>}
        </div>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)', marginBottom: 4, letterSpacing: '-0.01em' }}>
          {p.name}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--ink-3)', fontSize: 13 }}>
          <Icon name="pin" size={13}/>
          <span>{p.neighborhood} · {p.distance}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {p.ages.map(a => <Tag key={a}>{a}</Tag>)}
        </div>
      </div>
    </Card>
  </Reveal>
);

// Floating studs sprinkled behind the hero: purely decorative.
const heroStuds = [
  { size: 38, left: '6%',   top: 90,   color: '#F4C443', speed: 5.2 },
  { size: 22, left: '4%',   top: 318,  color: '#E23226', speed: 6.5 },
  { size: 30, left: '20%',  top: 360,  color: '#2EA45D', speed: 7.2 },
  { size: 46, right: '8%',  top: 70,   color: '#1A66D5', speed: 5.8 },
  { size: 26, right: '16%', top: 240,  color: '#F4C443', speed: 6.8 },
  { size: 32, right: '6%',  top: 400,  color: '#8E63CC', speed: 5.4 },
  { size: 18, left: '46%',  top: 30,   color: '#FF9F45', speed: 6.0 },
];

const AUDIENCE_COPY = {
  parents: {
    badge: 'For parents · live openings + resources',
    subtitle: 'Search open childcare spots, understand subsidy resources, save favorites, and book tours when you are ready, no account required to start.',
    trust: ['No account needed', 'ELRC, SNAP & CHIP resources', 'Save favorites when ready'],
    benefitTitle: 'Why families use Find My Childcare',
    benefitSubtitle: 'A calmer way to compare openings, understand resources, and move from search to tour.',
    benefits: [
      { icon: 'shield', title: 'Subsidy and benefits help', text: 'Plain-language resources for ELRC, SNAP, CHIP, and how to prepare for enrollment.' },
      { icon: 'star', title: 'Optional parent account', text: 'Save favorite centers, prefill tour details, join waitlists, and message programs.' },
      { icon: 'spark', title: 'Developmental resources', text: 'Add local supports like Elwyn and autism evaluation resources for families who need guidance.' },
    ],
  },
  providers: {
    badge: 'For providers · fill seats faster',
    subtitle: 'Stop chasing Facebook groups, online ads, and scattered waitlists. Keep openings live and receive family inquiries from trusted community referral channels.',
    trust: ['$65/month per provider', 'ELRC collaboration ready', 'Tour requests routed to you'],
    benefitTitle: 'Why providers join',
    benefitSubtitle: 'A practical enrollment layer for programs that want fewer scattered inquiries and more qualified tours.',
    benefits: [
      { icon: 'inbox', title: 'No more Facebook scramble', text: 'Share live openings where families are already being referred instead of posting in public groups.' },
      { icon: 'calendar-check', title: 'Turn openings into tours', text: 'Route families to virtual tours, in-person visits, calls, and waitlists from one profile.' },
      { icon: 'spark', title: 'Stay visible when spots open', text: 'Fresh availability helps your center show up first when families need care now.' },
    ],
  },
  partners: {
    badge: 'For ELRC, agency & nonprofit partners',
    subtitle: 'Turn live openings, affordability signals, waitlists, and family demand into a shared operating picture for referrals, policy, grants, and capacity-building.',
    trust: ['Real-time availability', 'Waitlist & demand trends', 'Funding decisions with evidence'],
    benefitTitle: 'How partners use the data',
    benefitSubtitle: 'A shared view of supply, demand, and provider capacity helps public and nonprofit teams act faster.',
    benefits: [
      { icon: 'grid', title: 'Real-time childcare landscape', text: 'See openings, age-group capacity, waitlist pressure, and affordability signals by neighborhood.' },
      { icon: 'dollar', title: 'Maximize public funding', text: 'Target grants, incentives, and expansion support where demand is highest and wait times are longest.' },
      { icon: 'shield', title: 'Strengthen provider networks', text: 'Help programs stay full while giving ELRCs and nonprofits a trusted referral path for families.' },
    ],
  },
};

const AccentWord = ({ children }) => (
  <span style={{ position: 'relative', whiteSpace: 'nowrap', display: 'inline-block' }}>
    <span style={{ position: 'relative', zIndex: 1 }}>{children}</span>
    <svg
      viewBox="0 0 280 14" preserveAspectRatio="none"
      style={{
        position: 'absolute', left: 0, right: 0, bottom: -2, width: '100%', height: '0.42em',
        zIndex: 0, color: 'var(--coral)',
      }}
      aria-hidden
    >
      <path
        className="underline-stroke"
        d="M2 9 C 60 2, 140 2, 278 8"
        stroke="currentColor" strokeWidth="6" strokeLinecap="round" fill="none" opacity="0.65"
      />
    </svg>
  </span>
);

const AudienceHeadline = ({ audience }) => {
  if (audience === 'providers') {
    return <>Fill open seats without the <AccentWord>Facebook scramble</AccentWord></>;
  }
  if (audience === 'partners') {
    return <>Turn childcare data into <AccentWord>access strategy</AccentWord> for the neighborhoods you serve.</>;
  }
  return <>Find childcare with <AccentWord>open spots</AccentWord> near you.</>;
};

const AudienceFork = ({ audience, onAudienceChange, go, panelId }) => {
  const options = [
    { id: 'parents', label: 'Parent' },
    { id: 'providers', label: 'Provider' },
    { id: 'partners', label: 'Partner' },
  ];

  const choose = (id) => {
    if (onAudienceChange) onAudienceChange(id);
    else go('home', { audience: id });
  };

  return (
    <div className="hero-audience-fork h-in h-in-1" aria-label="Choose your audience">
      <span className="hero-audience-label">I’m a</span>
      <div className="hero-audience-options" role="tablist" aria-label="Homepage audience">
        {options.map(option => {
          const active = audience === option.id;
          return (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={panelId}
              onClick={() => choose(option.id)}
              className={`hero-audience-option ${active ? 'is-active' : ''}`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const AudienceBenefitGrid = ({ benefits }) => (
  <div className="audience-benefits" style={{
    margin: '0 auto', maxWidth: 1000,
    display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12,
  }}>
    {benefits.map(item => (
      <div key={item.title} style={{
        background: 'rgba(255,255,255,0.68)', border: '1px solid rgba(226,210,174,0.70)',
        borderRadius: 18, padding: 16, textAlign: 'left', boxShadow: 'var(--shadow-sm)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{
            width: 34, height: 34, borderRadius: 11, flexShrink: 0,
            background: 'var(--brand-tint)', color: 'var(--brand)',
            display: 'grid', placeItems: 'center',
          }}>
            <Icon name={item.icon} size={16}/>
          </span>
          <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--ink)', lineHeight: 1.25 }}>{item.title}</div>
        </div>
        <p style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--ink-3)' }}>{item.text}</p>
      </div>
    ))}
  </div>
);

export const HomeScreen = ({
  go,
  audience = 'parents',
  onAudienceChange,
  familySearchRoute = 'results',
}) => {
  const [zip, setZip] = React.useState('19130');
  const [childrenCount, setChildrenCount] = React.useState(1);
  const [childrenAges, setChildrenAges] = React.useState(['Young Toddler']);
  const [schedule, setSchedule] = React.useState('Full time');

  // Keep childrenAges length in sync with childrenCount.
  React.useEffect(() => {
    setChildrenAges(prev => {
      if (prev.length === childrenCount) return prev;
      if (prev.length < childrenCount) {
        const fill = Array(childrenCount - prev.length).fill('Young Toddler');
        return [...prev, ...fill];
      }
      return prev.slice(0, childrenCount);
    });
  }, [childrenCount]);

  const setChildAge = (i, val) => setChildrenAges(prev => {
    const next = prev.slice();
    next[i] = val;
    return next;
  });

  const submitSearch = () => go(familySearchRoute, {
    zip,
    childrenAges: childrenAges.slice(0, childrenCount),
    schedule,
  });

  const copy = AUDIENCE_COPY[audience] || AUDIENCE_COPY.parents;
  const visible = PROVIDERS.slice(0, 3);

  return (
    <div className="route-in">
      {/* HERO ============================================== */}
      <section className="hero-section" style={{
        position: 'relative',
        paddingTop: 0, paddingBottom: 64,
        background: `
          radial-gradient(820px 360px at 88% -10%, rgba(244,196,67,0.32), transparent 60%),
          radial-gradient(820px 360px at 8% -4%, rgba(26,102,213,0.16), transparent 55%),
          radial-gradient(620px 280px at 50% 8%, rgba(226,50,38,0.08), transparent 60%),
          var(--bg)
        `,
        overflow: 'hidden',
      }}>
        <FloatingStuds studs={heroStuds}/>

        <div className="audience-bar-shell">
          <AudienceFork audience={audience} onAudienceChange={onAudienceChange} go={go} panelId="home-audience-panel"/>
        </div>

        <div id="home-audience-panel" className="container" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>

          <div className="hero-brand-mark h-in h-in-2" aria-hidden="true">
            <img src="/assets/logo.png" alt="" />
          </div>

          <h1 className="hero-headline h-in h-in-2" style={{
            fontSize: 'clamp(36px, 5.4vw, 64px)',
            fontFamily: 'var(--font-display)', fontWeight: 700,
            letterSpacing: '-0.025em', lineHeight: 1.04,
            color: 'var(--ink)',
            maxWidth: 880, margin: '0 auto',
          }}>
            <AudienceHeadline audience={audience}/>
          </h1>

          <p className="hero-sub h-in h-in-3" style={{
            marginTop: 18, fontSize: 18, lineHeight: 1.5,
            color: 'var(--ink-2)', maxWidth: 580, margin: '18px auto 0',
          }}>
            {copy.subtitle}
          </p>

          {audience === 'parents' ? (
            <div className="hero-search h-in h-in-4" style={{
              marginTop: 36, maxWidth: 820, marginLeft: 'auto', marginRight: 'auto',
              background: 'var(--surface)',
              borderRadius: 24,
              padding: 10,
              border: '1px solid var(--line)',
              boxShadow: '0 30px 60px -30px rgba(26,102,213,0.32), 0 1px 0 rgba(0,0,0,0.02)',
              display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'left',
            }}>
              {/* Row 1: ZIP · # children · schedule · CTA */}
              <div className="hero-search-row" style={{
                display: 'grid',
                gridTemplateColumns: '1.3fr 1px 1fr 1px 1fr auto',
                alignItems: 'stretch',
              }}>
                <label className="hero-search-cell" style={{
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  padding: '10px 18px', borderRadius: 18, cursor: 'text',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>ZIP code</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <Icon name="pin" size={14} style={{ color: 'var(--ink-3)' }}/>
                    <input
                      value={zip}
                      onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                      inputMode="numeric"
                      pattern="[0-9]{5}"
                      placeholder="19130"
                      style={{
                        border: 'none', outline: 'none', background: 'transparent',
                        fontSize: 15, fontWeight: 500, color: 'var(--ink)', width: '100%',
                      }}
                    />
                  </div>
                </label>

                <div className="hero-divider" style={{ width: 1, background: 'var(--line)', margin: '14px 0' }}/>

                <label className="hero-search-cell" style={{
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  padding: '10px 18px', cursor: 'pointer',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Children</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, position: 'relative' }}>
                    <Icon name="user" size={14} style={{ color: 'var(--ink-3)' }}/>
                    <select
                      value={childrenCount}
                      onChange={(e) => setChildrenCount(Number(e.target.value))}
                      style={{
                        border: 'none', outline: 'none', background: 'transparent',
                        fontSize: 15, fontWeight: 500, color: 'var(--ink)',
                        appearance: 'none', width: '100%', paddingRight: 18, cursor: 'pointer',
                      }}
                    >
                      {[1, 2, 3, 4, 5].map(n => (
                        <option key={n} value={n}>{n} {n === 1 ? 'child' : 'children'}</option>
                      ))}
                    </select>
                    <Icon name="chev-down" size={14} style={{ color: 'var(--ink-3)', position: 'absolute', right: 0 }}/>
                  </div>
                </label>

                <div className="hero-divider" style={{ width: 1, background: 'var(--line)', margin: '14px 0' }}/>

                <label className="hero-search-cell" style={{
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  padding: '10px 18px', cursor: 'pointer',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Schedule</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, position: 'relative' }}>
                    <Icon name="calendar" size={14} style={{ color: 'var(--ink-3)' }}/>
                    <select
                      value={schedule} onChange={(e) => setSchedule(e.target.value)}
                      style={{
                        border: 'none', outline: 'none', background: 'transparent',
                        fontSize: 15, fontWeight: 500, color: 'var(--ink)',
                        appearance: 'none', width: '100%', paddingRight: 18, cursor: 'pointer',
                      }}
                    >
                      {SCHEDULES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Icon name="chev-down" size={14} style={{ color: 'var(--ink-3)', position: 'absolute', right: 0 }}/>
                  </div>
                </label>

                <div className="hero-action" style={{ padding: 4, display: 'flex', alignItems: 'stretch' }}>
                  <Button
                    variant="primary" size="lg" icon="search"
                    onClick={submitSearch}
                    style={{ borderRadius: 18, paddingLeft: 22, paddingRight: 22 }}
                  >
                    Find Open Spots
                  </Button>
                </div>
              </div>

              {/* Row 2: per-child age dropdowns */}
              <div className="hero-search-ages" style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(childrenCount, 3)}, minmax(0, 1fr))`,
                gap: 6,
                borderTop: '1px solid var(--line)',
                paddingTop: 6,
              }}>
                {Array.from({ length: childrenCount }).map((_, i) => (
                  <label key={i} className="hero-search-cell" style={{
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    padding: '10px 18px', cursor: 'pointer', minWidth: 0,
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--ink-2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {childrenCount === 1 ? 'Age' : `Child ${i + 1} age`}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, position: 'relative', minWidth: 0 }}>
                      <Icon name="heart" size={14} style={{ color: 'var(--ink-3)' }}/>
                      <select
                        value={childrenAges[i] || 'Young Toddler'}
                        onChange={(e) => setChildAge(i, e.target.value)}
                        style={{
                          border: 'none', outline: 'none', background: 'transparent',
                          fontSize: 15, fontWeight: 500, color: 'var(--ink)',
                          appearance: 'none', width: '100%', paddingRight: 18, cursor: 'pointer',
                          minWidth: 0, textOverflow: 'ellipsis',
                        }}
                      >
                        {AGE_GROUPS.map(g => (
                          <option key={g} value={g}>{g} ({AGE_GROUP_RANGES[g]})</option>
                        ))}
                      </select>
                      <Icon name="chev-down" size={14} style={{ color: 'var(--ink-3)', position: 'absolute', right: 0 }}/>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-in h-in-4" style={{
              margin: '34px auto 0', maxWidth: 760,
              display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap',
            }}>
              <Button
                variant="primary" size="xl" icon={audience === 'providers' ? 'calendar-check' : 'grid'}
                onClick={() => go(audience === 'partners' ? 'partner' : 'onboarding')}
              >
                {audience === 'providers' ? 'See how it works for your center' : 'See the neighborhood data'}
              </Button>
              <Button variant="secondary" size="xl" iconRight="arrow-right" onClick={() => go(familySearchRoute)}>
                See the family view
              </Button>
            </div>
          )}

          <div className="h-in h-in-5" style={{
            margin: '18px auto 0',
            display: 'inline-flex', gap: 10, flexWrap: 'wrap',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(255,255,255,0.80)', border: '1px solid rgba(226,210,174,0.72)',
            boxShadow: 'var(--shadow-sm)', borderRadius: 999, padding: '8px 12px',
          }}>
            {copy.trust.map((t, i) => (
              <React.Fragment key={t}>
                {i > 0 && <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>·</span>}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-2)', fontWeight: 700 }}>
                  <Icon name="check" size={14} style={{ color: 'var(--brand)' }}/>{t}
                </span>
              </React.Fragment>
            ))}
            {audience === 'parents' && (
              <>
                <span style={{ color: 'var(--ink-4)', fontSize: 12 }}>·</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--brand-ink)', fontWeight: 900 }}>
                  <Icon name="spark" size={14} style={{ color: 'var(--coral)' }}/>
                  Provider-confirmed availability
                </span>
              </>
            )}
          </div>
        </div>
      </section>

      {/* PROVIDER GRID (preview) ============================ */}
      <section style={{ padding: '40px 0 72px' }}>
        <div className="container">
          <Reveal>
            <div style={{
              display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
              marginBottom: 24, gap: 16, flexWrap: 'wrap',
            }}>
              <div>
                <Badge tone="brand" icon="live" style={{ marginBottom: 10 }}>
                  Illustrative Philadelphia pilot
                </Badge>
                <h2 style={{
                  fontSize: 'clamp(24px, 3.5vw, 32px)', fontWeight: 800, color: 'var(--ink)',
                  letterSpacing: '-0.02em',
                }}>
                  Open near Philadelphia, PA
                </h2>
                <p style={{ color: 'var(--ink-3)', marginTop: 6, fontSize: 15 }}>
                  Example provider cards showing how current openings could appear
                </p>
              </div>
              <button
                onClick={() => go(familySearchRoute)}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--brand)', fontWeight: 600, fontSize: 14,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                See all results <Icon name="arrow-right" size={14}/>
              </button>
            </div>
          </Reveal>

          <div style={{
            display: 'grid', gap: 20,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          }}>
            {visible.map((p, i) => <HomeProviderCard key={p.id} p={p} go={go} delay={i * 90}/>)}
          </div>
        </div>
      </section>

      {/* AUDIENCE BENEFITS ================================== */}
      <section style={{ padding: '0 0 72px' }}>
        <div className="container">
          <Reveal>
            <div style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 24px' }}>
              <Badge tone="brand" style={{ marginBottom: 12 }}>{copy.badge}</Badge>
              <h2 style={{
                fontSize: 'clamp(25px, 3.2vw, 34px)', fontWeight: 900,
                color: 'var(--ink)', letterSpacing: '-0.025em',
              }}>
                {copy.benefitTitle}
              </h2>
              <p style={{ color: 'var(--ink-3)', marginTop: 8, fontSize: 16, lineHeight: 1.55 }}>
                {copy.benefitSubtitle}
              </p>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <AudienceBenefitGrid benefits={copy.benefits}/>
          </Reveal>
        </div>
      </section>

      {/* PROVIDER CTA ====================================== */}
      <section style={{ padding: '0 0 96px' }}>
        <div className="container">
          <Reveal>
            <div className="cta-grid" style={{
              position: 'relative', overflow: 'hidden',
              borderRadius: 'var(--r-xl)',
              background: 'linear-gradient(135deg, #0E408E 0%, #1A66D5 60%, #3B82E8 100%)',
              color: '#fff',
              padding: '48px clamp(28px, 5vw, 64px)',
              display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 36, alignItems: 'center',
            }}>
              {/* LEGO studs decoration */}
              <svg viewBox="0 0 200 200" style={{
                position: 'absolute', right: -30, top: -30, width: 320, opacity: 0.22,
              }} aria-hidden>
                <defs>
                  <pattern id="studs" width="22" height="22" patternUnits="userSpaceOnUse">
                    <circle cx="11" cy="11" r="5" fill="#fff" opacity="0.9"/>
                    <circle cx="11" cy="11" r="5" fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="0.6"/>
                  </pattern>
                </defs>
                <rect width="200" height="200" fill="url(#studs)"/>
              </svg>

              <div style={{ position: 'relative', zIndex: 1 }}>
                <Badge tone="yellow" style={{ marginBottom: 18 }}>For Providers</Badge>
                <h2 style={{
                  fontSize: 'clamp(28px, 3.6vw, 42px)', fontWeight: 700,
                  letterSpacing: '-0.02em', lineHeight: 1.08,
                  marginBottom: 14,
                }}>
                  Fill seats through <br/>
                  trusted referrals.
                </h2>
                <p style={{ fontSize: 16, lineHeight: 1.55, opacity: 0.88, maxWidth: 480 }}>
                  Fill seats with families already moving through trusted referral channels.
                  Keep openings current, route tours cleanly, and make your program easier to find.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 28, flexWrap: 'wrap' }}>
                  <Button
                    variant="yellow" size="xl"
                    onClick={() => go('provider-access')}
                  >
                    List your center
                  </Button>
                  <span style={{ fontSize: 13, opacity: 0.75 }}>
                    Working price: $65/month
                  </span>
                </div>
              </div>

              <div className="cta-stat" style={{ position: 'relative', zIndex: 1 }}>
                <div style={{
                  background: 'rgba(255,255,255,0.10)',
                  border: '1px solid rgba(255,255,255,0.20)',
                  borderRadius: 18, padding: 22,
                  backdropFilter: 'blur(6px)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <span style={{
                      width: 36, height: 36, borderRadius: 10,
                      background: 'rgba(255,255,255,0.14)',
                      display: 'grid', placeItems: 'center',
                    }}>
                      <Icon name="spark" size={18}/>
                    </span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        Provider tools in one place
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.72 }}>
                        Prototype capabilities shown with illustrative data
                      </div>
                    </div>
                  </div>
                  <div style={{
                    height: 1, background: 'rgba(255,255,255,0.12)', margin: '14px 0 18px',
                  }}/>
                  <ul style={{
                    margin: 0, padding: 0, listStyle: 'none',
                    display: 'grid', gap: 10, fontSize: 14,
                  }}>
                    {[
                      'Current openings and freshness checks',
                      'Tours and waitlists',
                      'Provider profile and family updates',
                    ].map(t => (
                      <li key={t} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <Icon name="check" size={14} style={{ color: 'var(--yellow)' }}/>{t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <footer style={{
        borderTop: '1px solid var(--line)', padding: '38px 0 46px',
        color: 'var(--ink-3)', fontSize: 13, background: 'rgba(255,255,255,0.30)',
      }}>
        <div className="container site-footer-grid">
          <div>
            <button onClick={() => go('home')} style={{
              display: 'inline-flex', alignItems: 'center', gap: 10, border: 'none',
              background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--ink)',
              fontWeight: 900, fontSize: 16, letterSpacing: '-0.03em',
            }}>
              <img src="/assets/logo.png" alt="" loading="lazy" style={{ width: 26, height: 32, objectFit: 'contain' }}/>
              Find My Childcare
            </button>
            <p style={{ marginTop: 12, maxWidth: 340, color: 'var(--ink-3)', lineHeight: 1.55 }}>
              A calmer way for Philadelphia families to find live childcare openings and trusted next steps.
            </p>
            <div style={{ marginTop: 14 }}>© 2026 Find My Childcare</div>
          </div>
          <div>
            <h3 style={{ fontSize: 13, fontWeight: 900, color: 'var(--ink)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>For Families</h3>
            <div className="footer-links">
              <button className="footer-link" onClick={() => go(familySearchRoute)}>Browse</button>
              <button className="footer-link" onClick={() => go('requests')}>My requests</button>
              <button className="footer-link" onClick={() => go('home', { audience: 'parents' })}>How it works</button>
              <button className="footer-link" onClick={() => go('resources')}>Child Care Works</button>
            </div>
          </div>
          <div>
            <h3 style={{ fontSize: 13, fontWeight: 900, color: 'var(--ink)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>For Providers</h3>
            <div className="footer-links">
              <button className="footer-link" onClick={() => go('provider-access')}>List your center</button>
              <button className="footer-link" onClick={() => go('home', { audience: 'providers' })}>Pricing</button>
              <button className="footer-link" onClick={() => go('provider-access')}>Sign in</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
